// AgentLogin — the agent / franchise login page.
//
// Visually distinct from /admin/login (different colour scheme,
// different copy) so a agent instantly knows they're at the
// agent door and not the platform-owner door. Posts the same
// /api/admin/auth/login endpoint with `expected_role='agent'` —
// backend rejects role mismatches with a generic 401 (per plan
// §4.1 v3.1 — security through obscurity).
//
// After successful login both roles enter the same `/admin/*` shell.
// Role-based gating inside the shell decides which pages render.

import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import Icon from '@baaligames/shared-ui/components/Icon';
import BaaliLogo from '@baaligames/shared-ui/admin/components/BaaliLogo';

export default function AgentLogin() {
  const t = useT();
  const { admin, login, loading } = useAdminAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && admin) nav('/', { replace: true });
  }, [admin, loading, nav]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      await login(identifier, password, 'agent');
      nav('/', { replace: true });
    } catch (e) {
      // Auth errors arrive as ApiError from the shared admin auth lib.
      // The server's own message wins (it's already locale-aware for
      // the most common 401s); we fall back to a translator-managed
      // generic only when the server didn't ship one.
      setError((e as ApiError).message || t('agent.login.error.loginFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="min-h-screen min-h-dvh grid place-items-center px-4
                 bg-gradient-to-br from-slate-950 via-cyan-950 to-emerald-950 text-neutral-100"
      style={{
        paddingTop:    'max(1rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
        paddingLeft:   'max(1rem, env(safe-area-inset-left))',
        paddingRight:  'max(1rem, env(safe-area-inset-right))',
      }}>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 220, damping: 24 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-6 flex flex-col items-center">
          <div className="mb-3">
            <BaaliLogo role="agent" wordmark={false} size={64} />
          </div>
          <h1 className="text-2xl font-bold">{t('agent.login.brand')}</h1>
          <p className="text-sm text-neon-200/80 dark:text-neon-200/80">{t('agent.login.subtitle')}</p>
        </div>
        <form onSubmit={submit}
              className="rounded-2xl bg-neutral-900/70 backdrop-blur border border-emerald-700/30 p-5 space-y-4 shadow-2xl">
          <div>
            <label className="block text-xs font-semibold text-neon-200/85 mb-1.5 dark:text-neon-200/85">
              {t('agent.login.field.identifier')}
            </label>
            <div className="relative">
              <Icon name="person" size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-neon-200/75 dark:text-neon-200/75" />
              <input
                className="w-full rounded-xl bg-arena-800 border border-neutral-700
                           pl-10 pr-3 py-2.5 text-base text-neutral-100
                           focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                style={{ fontSize: 16 }}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-neon-200/85 mb-1.5 dark:text-neon-200/85">{t('agent.login.field.password')}</label>
            <div className="relative">
              <Icon name="lock" size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-neon-200/75 dark:text-neon-200/75" />
              <input
                type="password"
                className="w-full rounded-xl bg-arena-800 border border-neutral-700
                           pl-10 pr-3 py-2.5 text-base text-neutral-100
                           focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                style={{ fontSize: 16 }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
          </div>
          {error && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-sm px-3 py-2">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl
                       bg-gradient-to-r from-emerald-600 to-cyan-700
                       hover:from-emerald-500 hover:to-cyan-600
                       text-white font-semibold py-2.5 shadow-lg
                       transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? <Icon name="progress_activity" size={18} className="animate-spin" /> : <Icon name="login" size={18} />}
            {/* Reuse the cross-app login keys (login.signingIn /
                common.signIn) so the verb form stays consistent with
                the player-end sign-in CTA. */}
            {busy ? t('login.signingIn') : t('common.signIn')}
          </button>
          <p className="text-center text-[11px] text-neon-200/80 pt-1 dark:text-neon-200/80">
            {t('agent.login.disclaimer.before')}
            <a href="/login" className="text-emerald-400 hover:underline">{t('agent.login.disclaimer.link')}</a>
            {t('agent.login.disclaimer.after')}
          </p>
        </form>
      </motion.div>
    </div>
  );
}
